import React from "react";
import ImgCrop from "antd-img-crop";
import { Popover, Upload } from "antd";
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";

import "./index.css";
import { errorMessage, getApi, successMessage } from "../../helpers";

export default function UploadImage({
  title = "Add Category Image",
  apiName = "uploadCategoryImage",
  type = "category",
  type_id = 7,
  variant_id = 0,
  setLoading = () => null,
  onUploadDone = () => null,
  uploadLaterHandler = () => null,
  done = false,
  uploadLater = false,
  miniBtn = false,
  bannerImage = false,
  aspectRatio = null,
  ...otherProps
}) {
  const uploadHandler = async ({ onSuccess, onError, file, onProgress }) => {
    setLoading(true);
    const fmData = new FormData();
    const headers = { "content-type": "multipart/form-data" };
    if (variant_id) {
      fmData.append("variant_id", variant_id);
    }
    fmData.append(type + "_id", type_id);
    fmData.append(type + "_img", file);
    try {
      const res = await getApi(fmData, apiName, "ecomm/", headers);
      onSuccess("Ok");
      let msg = "Category";
      if (bannerImage) msg = "Banner";
      else if (variant_id) msg = "Variant";
      else if (type === "offer") msg = "Offer";
      else if (type === "sub_category") msg = "Sub Category";
      successMessage(msg + " Image Uploaded");
      setLoading(false);
      onUploadDone(res);
    } catch (err) {
      const error = new Error("Some error");
      console.log("Error: ", err, error);
      errorMessage("Image Upload Failed");
      onError({ err });
      setLoading(false);
    }
  };

  // Calculate aspect ratio based on image type
  const getAspectRatio = () => {
    // If custom aspect ratio is provided, use it
    if (aspectRatio !== null) {
      return aspectRatio;
    }
    if (bannerImage) {
      return 358 / 170; // 2.1:1 aspect ratio for new banners (358x170)
    }
    return 1 / 1; // Default 1:1 for other images
  };

  // Get crop dimensions text for info popover
  const getCropDimensions = () => {
    if (bannerImage) {
      return "358 x 170";
    }
    return miniBtn ? "200 x 200" : "500 x 500";
  };

  // Upload component with cropping (for all images including banners)
  const UploadWithCrop = () => (
    <ImgCrop
      grid
      modalOk="Save"
      modalTitle={title}
      fillColor="transparent"
      minZoom={0.4}
      cropperProps={{ restrictPosition: false }}
      aspect={getAspectRatio()}
    >
      <Upload
        accept={bannerImage ? "image/jpeg,image/jpg,image/png" : "image/png"}
        listType="picture-card"
        className={
          bannerImage ? "banner-uplodr" : miniBtn ? "mini-uplodr" : "uplodr"
        }
        customRequest={!uploadLater ? uploadHandler : uploadLaterHandler}
        multiple={false}
        {...otherProps}
      >
        {!done && (
          <div style={{ position: "relative" }}>
            {!bannerImage && (
              <Popover
                className={miniBtn ? "mini-infop" : "infop"}
                content={
                  <ul style={{ padding: "0 5px" }}>
                    <li>
                      A dimension of atleast {getCropDimensions()}
                    </li>
                    <li>An extension of {bannerImage ? "JPEG, JPG, or PNG" : "PNG"}</li>
                    <li>An aspect ratio of {bannerImage ? "358:170" : "1:1"}</li>
                  </ul>
                }
                title="Image must have :"
                placement="leftTop"
                trigger={["click", "hover"]}
              >
                <InfoCircleOutlined />
              </Popover>
            )}
            <PlusOutlined />
            <div style={{ marginTop: miniBtn ? 2 :4 }}>
              Upload
              {bannerImage && (
                <div style={{ margin: "0 22%" }}>
                  <br />
                  <span className="banner-infop">
                    Image will be cropped to 358x170 pixels (aspect ratio 358:170). Supports JPEG, JPG, and PNG formats.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </Upload>
    </ImgCrop>
  );

  // Upload component without cropping - remove this for banners
  const UploadWithoutCrop = () => (
    <Upload
      accept={bannerImage ? "image/jpeg" : "image/png"}
      listType="picture-card"
      className={
        bannerImage ? "banner-uplodr" : miniBtn ? "mini-uplodr" : "uplodr"
      }
      customRequest={!uploadLater ? uploadHandler : uploadLaterHandler}
      multiple={false}
      {...otherProps}
    >
      {!done && (
        <div style={{ position: "relative" }}>
          <PlusOutlined />
          <div style={{ marginTop: miniBtn ? 2 : 8 }}>
            Upload
            {bannerImage && (
              <div style={{ margin: "0 22%" }}>
                <br />
                <span className="banner-infop">
                  Make sure it's a JPEG, JPG, or PNG image and not larger than 3456 x 1728
                  or 5MB with aspect ratio of 16:9.
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </Upload>
  );

  // Always use cropping component now
  return <UploadWithCrop />;
}
